import { addDays, addMonths, eachDayOfInterval, endOfMonth, endOfWeek, startOfMonth, startOfWeek } from 'date-fns';

import { dateToIso, isoToDate } from '@/lib/utils/date';

/** Six rows of seven. */
export const GRID_CELLS = 42;

/**
 * The days a month grid covers.
 *
 * A month grid is not a month: it starts on the Sunday on or before the 1st and
 * always runs 42 days, so February and August occupy the same six rows and the
 * page does not change height when you step between them. February 2026 is the
 * extreme case — 28 days beginning on a Sunday, where fourteen of the cells
 * belong to March.
 *
 * ## Two things this file is careful about
 *
 * **Every Date here is a local midnight.** `isoToDate` parses date-only strings
 * as local rather than UTC, and `dateToIso` formats locally, so a value can go
 * out and come back unchanged. There is no UTC round trip anywhere, which is
 * what stops this producing the off-by-one that `lib/utils/date.ts` exists to
 * prevent.
 *
 * **`addDays` adds calendar days, not 24 hours.** Across a DST transition a
 * 24-hour step lands at 23:00 the previous day or 01:00 the next, and a grid
 * built that way duplicates or skips a day twice a year.
 *
 * ## Where the window comes from
 *
 * This is the only implementation of the rule. The API takes explicit
 * `date_from`/`date_to` bounds rather than a month, so the client owns the grid
 * shape and the server never has to learn one. The Laravel original keeps a
 * second copy in PHP because its first paint is server-rendered; nothing here
 * renders on the server, so there is nothing to keep in step.
 */
export function buildMonthGrid(month: string): Date[] {
  const anchor = isoToDate(`${month}-01`);

  if (anchor === undefined) {
    throw new Error(`buildMonthGrid: expected YYYY-MM, received "${month}"`);
  }

  const days = eachDayOfInterval({
    end: endOfWeek(endOfMonth(anchor), { weekStartsOn: 0 }),
    // Sunday, passed explicitly rather than left to the locale. A
    // locale-dependent grid would silently change shape for a different
    // browser, and the weekday header row above it is hardcoded to Sun-first.
    start: startOfWeek(startOfMonth(anchor), { weekStartsOn: 0 }),
  });

  // February beginning on a Sunday yields 28 — the only case short of six rows.
  while (days.length < GRID_CELLS) {
    days.push(addDays(days[days.length - 1], 1));
  }

  return days;
}

/**
 * Buckets entries by the raw `encounter_date` string.
 *
 * String keys in, string keys out — no Date is constructed from a value that
 * carries no timezone. `encounter_date` decides the cell; `scheduled_at` only
 * decides the label inside it, which is why an entry whose instant falls on a
 * different day still buckets under the date the clinic recorded.
 */
export function groupByDate<T extends { encounter_date: string }>(entries: T[]): Map<string, T[]> {
  const byDate = new Map<string, T[]>();

  for (const entry of entries) {
    const bucket = byDate.get(entry.encounter_date);

    if (bucket === undefined) {
      byDate.set(entry.encounter_date, [entry]);
    } else {
      bucket.push(entry);
    }
  }

  return byDate;
}

/**
 * The month a practice is currently in.
 *
 * Takes the organization's today rather than the browser's. For a UTC+11
 * practice a device in another zone is on the previous month for part of every
 * 1st, so a local default would open the calendar on the wrong month.
 */
export function monthOf(isoDate: string): string {
  return isoDate.slice(0, 7);
}

/** The inclusive `YYYY-MM-DD` bounds of a month's grid. */
export function monthRange(month: string): { from: string; to: string } {
  const days = buildMonthGrid(month);

  return { from: dateToIso(days[0]), to: dateToIso(days[days.length - 1]) };
}

/** `'2026-08'` + 1 → `'2026-09'`. */
export function shiftMonth(month: string, delta: number): string {
  const anchor = isoToDate(`${month}-01`);

  if (anchor === undefined) {
    throw new Error(`shiftMonth: expected YYYY-MM, received "${month}"`);
  }

  return dateToIso(addMonths(anchor, delta)).slice(0, 7);
}
